import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type ConditionalAmountNodeParameters = {
    description: string
    amount: number
    condition: VariantConditionNode
}

const conditionalAmountNodeDefinition = {
    nodeClass: "ConditionalAmountNode",
    validation: {
        parameters: z.object({
            description: z.string(),
            amount: z.number(),
            condition: graphNode(VariantConditionNode),
        }),
        returns: z.number(),
    },
} satisfies GraphNodeDefinition<number, ConditionalAmountNodeParameters>

@registerPricingGraphNode
export class ConditionalAmountNode extends PricingNode<number, ConditionalAmountNodeParameters> {
    public override get definition() {
        return conditionalAmountNodeDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        if (this.parameters.condition.getVariantIds().length === 0) return 0 // this means that the condition was not set up yet. In contrast to the priced item node, this node is assumed to be always conditional.
        const condition = await evaluator.get(this.parameters.condition)
        return condition ? this.parameters.amount : 0
    }

    addDependency(configGroupNode: ConfigurationGroupNode, variantId: string, variantName: string) {
        this.parameters.condition.addDependency(configGroupNode, variantId, variantName)
    }

    removeDependency(variantId: string) {
        this.parameters.condition.removeDependency(variantId)
    }

    canAddDependency(groupId: string, variantId: string) {
        return this.parameters.condition.canAddDependency(groupId, variantId)
    }

    getVariantIds(): string[] {
        return this.parameters.condition.getVariantIds()
    }
}
